import {
  useCallback, useEffect, useRef, useState,
} from 'react';

const VOLUME_KEY = 'soundPreviewVolume';

const readVolume = () => {
  try {
    const raw = window.localStorage.getItem(VOLUME_KEY);
    const saved = Number(raw);
    return raw !== null && saved >= 0 && saved <= 100 ? saved : 80;
  } catch (e) {
    return 80;
  }
};

// Проигрывает mp3 по очереди. Отсутствующий файл пропускается — как и на табло.
export default function useSoundPreview() {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(readVolume);
  const queueRef = useRef([]);
  const audioRef = useRef(null);
  const volumeRef = useRef(volume);

  // Громкость 0–100; применяется и к уже играющему звуку
  const setVolume = useCallback((next) => {
    volumeRef.current = next;
    setVolumeState(next);
    if (audioRef.current) audioRef.current.volume = next / 100;
    try {
      window.localStorage.setItem(VOLUME_KEY, String(next));
    } catch (e) {
      // хранилище недоступно — громкость просто не запомнится
    }
  }, []);

  const stop = useCallback(() => {
    queueRef.current = [];
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  }, []);

  const playNext = useCallback(() => {
    const src = queueRef.current.shift();
    if (!src) {
      stop();
      return;
    }
    const audio = new Audio(src);
    audio.volume = volumeRef.current / 100;
    audioRef.current = audio;
    // Для отсутствующего файла браузер шлёт и error, и отказ play() —
    // без защиты очередь раздваивается и звуки играют поверх друг друга.
    // Чужой (уже остановленный) звук очередь тоже не двигает.
    let advanced = false;
    const next = () => {
      if (advanced || audioRef.current !== audio) return;
      advanced = true;
      playNext();
    };
    audio.onended = next;
    audio.onerror = next;
    audio.play().catch(next);
  }, [stop]);

  const play = useCallback((sources) => {
    stop();
    queueRef.current = [...sources];
    setPlaying(true);
    playNext();
  }, [stop, playNext]);

  useEffect(() => stop, [stop]);

  return {
    playing, play, stop, volume, setVolume,
  };
}
